/**
 * CUP_HANDLE — Cup & Handle Pattern Overlay
 *
 * Draws directly on the candle pane (isStack=true, paneId='candle_pane').
 *
 * Ported and extended from the base/cup detection logic in the MarketSmith Pine
 * Script reference (rolling rim window, leg-up validation, depth/length gates,
 * rounded-bottom shape check). The Pine source has no handle detection or
 * confidence score — both are new here:
 *
 *   - Handle: once the cup's right side climbs back near the rim, track a
 *     shallow pullback confined to the upper half of the cup, within a
 *     configurable depth/length band, optionally scored by volume dry-up.
 *   - Confidence (0-100): weighted blend of depth fit, length fit, a
 *     continuous U-shape "roundness" score (replacing Pine's binary cupForm
 *     gate), rim symmetry, handle quality, and prior-uptrend strength.
 *
 * Only the single most-recent qualifying pattern is tracked and drawn.
 */

import { IndicatorSeries, type Bounding, type IndicatorTemplate, type IndicatorDrawParams } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import type { CupHandleSettings } from '@/types';

// ── Types ───────────────────────────────────────────────────────────────────

type KBar = { open: number; high: number; low: number; close: number; volume?: number | null };

export interface CupHandlePattern {
  leftRimIndex: number;
  leftRimPrice: number;
  cupLowIndex: number;
  cupLowPrice: number;
  rightRimIndex: number;
  rightRimPrice: number;
  handle: {
    startIndex: number;
    startPrice: number;
    lowIndex: number;
    lowPrice: number;
    endIndex: number;
    depthPct: number;
    lengthBars: number;
    isComplete: boolean;
  } | null;
  breakoutPrice: number;
  confidence: number;
  subScores: {
    depth: number;
    length: number;
    roundness: number;
    symmetry: number;
    handle: number;
    priorTrend: number;
  };
  hasHandle: boolean;
}

export const DEFAULT_CUP_HANDLE_SETTINGS: CupHandleSettings = {
  pivotBars: 9,
  rimWindowBars: 25,
  priorLegBars: 60,
  minLegUpPct: 0.20,
  minLengthBars: 30,
  maxLengthBars: 325,
  maxDepthPct: 0.5,
  handleTriggerPct: 0.08,
  maxHandleDepthPct: 0.5,
  minHandleBars: 5,
  maxHandleBars: 65,
  minConfidenceToShow: 0,
};

// ── Small numeric helpers ──────────────────────────────────────────────────

function maxIn(arr: number[], from: number, to: number): number {
  let m = -Infinity;
  for (let i = from; i <= to; i++) if (arr[i] > m) m = arr[i];
  return m;
}
function minIn(arr: number[], from: number, to: number): number {
  let m = Infinity;
  for (let i = from; i <= to; i++) if (arr[i] < m) m = arr[i];
  return m;
}
function avgIn(arr: (number | null | undefined)[], from: number, to: number): number | null {
  let sum = 0, count = 0;
  for (let i = from; i <= to; i++) {
    const v = arr[i];
    if (v != null) { sum += v; count++; }
  }
  return count > 0 ? sum / count : null;
}

// Triangular scoring: rises from a 30 floor at `lo` to 100 at `mid`, back down to
// a 30 floor at `hi`. Values outside [lo,hi] (e.g. a pattern still forming) clamp
// to the 30 floor rather than falling to 0 — a valid-but-imperfect fit still counts.
function triangularScore(value: number, lo: number, mid: number, hi: number): number {
  if (value <= lo || value >= hi) return 30;
  if (value <= mid) return 30 + ((value - lo) / (mid - lo)) * 70;
  return 30 + ((hi - value) / (hi - mid)) * 70;
}

// Continuous version of the Pine source's binary `cupForm` check: splits the
// cup into thirds and scores how well closes cluster above the midline in the
// outer thirds and below it in the middle third (a rounded U, not a V-spike).
function computeRoundness(closes: number[], rim: number, rightEdge: number, midpoint: number): number {
  const span = rightEdge - rim;
  if (span < 3) return 50;
  const thirdLen = span / 3;
  const t1End = rim + thirdLen;
  const t2End = rim + thirdLen * 2;
  let outerAbove = 0, outerTotal = 0, middleBelow = 0, middleTotal = 0;
  for (let i = rim; i <= rightEdge; i++) {
    const inMiddle = i > t1End && i < t2End;
    if (inMiddle) {
      middleTotal++;
      if (closes[i] <= midpoint) middleBelow++;
    } else {
      outerTotal++;
      if (closes[i] >= midpoint) outerAbove++;
    }
  }
  const outerFrac = outerTotal > 0 ? outerAbove / outerTotal : 0.5;
  const middleFrac = middleTotal > 0 ? middleBelow / middleTotal : 0.5;
  return Math.round(((outerFrac + middleFrac) / 2) * 100);
}

function findPivotHighs(highs: number[], pivotBars: number): boolean[] {
  const n = highs.length;
  const pivotHigh = new Array<boolean>(n).fill(false);
  for (let i = pivotBars; i < n - pivotBars; i++) {
    let isHigh = true;
    for (let k = i - pivotBars; k <= i + pivotBars; k++) {
      if (k === i) continue;
      if (highs[k] > highs[i]) { isHigh = false; break; }
    }
    pivotHigh[i] = isHigh;
  }
  return pivotHigh;
}

// ── Candidate evaluation ────────────────────────────────────────────────────

function evaluateCandidate(
  highs: number[], lows: number[], closes: number[], vols: (number | null | undefined)[],
  rim: number, cfg: CupHandleSettings, n: number,
): CupHandlePattern | null {
  const rimPrice = highs[rim];
  const maxScan = Math.min(n - 1, rim + cfg.maxLengthBars);
  if (maxScan - rim < cfg.minLengthBars) return null;

  // First bar (if any) after the rim where price has broken back above it —
  // that ends the base/cup phase. If none yet, the cup is still forming.
  let baseEnd = maxScan;
  let broke = false;
  for (let i = rim + 1; i <= maxScan; i++) {
    if (highs[i] > rimPrice) { baseEnd = i; broke = true; break; }
  }
  if (broke && baseEnd - rim < cfg.minLengthBars) return null;

  // Cup low = lowest low between the rim and the right edge evaluated so far.
  let cupLowIdx = rim;
  let cupLowPrice = rimPrice;
  for (let i = rim + 1; i <= baseEnd; i++) {
    if (lows[i] < cupLowPrice) { cupLowPrice = lows[i]; cupLowIdx = i; }
  }
  if (cupLowIdx === rim) return null;

  const depthPct = (rimPrice - cupLowPrice) / rimPrice;
  if (depthPct < 0.08 || depthPct > cfg.maxDepthPct) return null;

  const rightEdge = baseEnd;
  const lengthBars = rightEdge - rim;
  if (lengthBars < cfg.minLengthBars) return null;

  // Price must have recovered to at least the cup's midpoint — otherwise it's
  // still just a base with an unconfirmed bottom, not a rounding cup.
  const midpoint = cupLowPrice + (rimPrice - cupLowPrice) * 0.5;
  const highestAfterLow = maxIn(highs, cupLowIdx, rightEdge);
  if (highestAfterLow < midpoint) return null;

  const rightRimIdx = rightEdge;
  const rightRimPrice = highs[rightEdge];

  const roundness = computeRoundness(closes, rim, rightEdge, midpoint);
  const depthScore = triangularScore(depthPct, 0.08, 0.20, cfg.maxDepthPct);
  const lengthScore = triangularScore(lengthBars, cfg.minLengthBars, (cfg.minLengthBars + cfg.maxLengthBars) / 2, cfg.maxLengthBars);
  const symmetryScore = Math.max(0, 100 - Math.min(100, (Math.abs(rightRimPrice - rimPrice) / rimPrice) * 400));

  const legStart = Math.max(0, rim - cfg.priorLegBars);
  const legLow = legStart < rim ? minIn(lows, legStart, rim) : rimPrice;
  const legPct = legLow > 0 ? (rimPrice - legLow) / legLow : 0;
  const priorTrendScore = Math.max(0, Math.min(100, ((legPct - cfg.minLegUpPct) / Math.max(cfg.minLegUpPct, 0.01)) * 100));

  // ── Handle: only once the right side has climbed back near the rim ───────
  let handle: CupHandlePattern['handle'] = null;
  const nearRim = rightRimPrice >= rimPrice * (1 - cfg.handleTriggerPct);
  if (nearRim && rightRimIdx < maxScan) {
    const searchStart = rightRimIdx;
    const searchEnd = maxScan;
    let hLow = highs[searchStart];
    let hLowIdx = searchStart;
    let hEnd = searchStart;
    for (let i = searchStart + 1; i <= searchEnd; i++) {
      if (i - searchStart > cfg.maxHandleBars) break;
      if (highs[i] > rightRimPrice && (i - searchStart) >= cfg.minHandleBars) {
        hEnd = i - 1;
        break;
      }
      if (lows[i] < hLow) { hLow = lows[i]; hLowIdx = i; }
      hEnd = i;
    }
    const cupDepth = rimPrice - cupLowPrice;
    const handleDepthPct = (rightRimPrice - hLow) / rightRimPrice;
    const handleLengthBars = hEnd - searchStart;
    const handleInUpperHalf = hLow >= cupLowPrice + cupDepth * 0.5;
    const handleDepthOk = handleDepthPct <= 0.15 || handleDepthPct <= cfg.maxHandleDepthPct * (cupDepth / rimPrice);
    if (handleInUpperHalf && handleDepthOk && handleLengthBars >= 1) {
      handle = {
        startIndex: searchStart, startPrice: rightRimPrice,
        lowIndex: hLowIdx, lowPrice: hLow,
        endIndex: hEnd,
        depthPct: handleDepthPct,
        lengthBars: handleLengthBars,
        isComplete: handleLengthBars >= cfg.minHandleBars,
      };
    }
  }

  let handleScore = 0;
  if (handle) {
    const depthComponent = handle.depthPct <= 0.15 ? 100 : Math.max(0, 100 - (handle.depthPct - 0.15) * 400);
    const lengthComponent = triangularScore(handle.lengthBars, cfg.minHandleBars, (cfg.minHandleBars + cfg.maxHandleBars) / 2, cfg.maxHandleBars);
    let volumeBonus = 50; // neutral when no volume data is available
    if (vols.some((v) => v != null)) {
      const cupVolAvg = avgIn(vols, rim, rightRimIdx);
      const handleVolAvg = avgIn(vols, handle.startIndex, handle.endIndex);
      if (cupVolAvg != null && handleVolAvg != null && cupVolAvg > 0) {
        volumeBonus = handleVolAvg < cupVolAvg ? 100 : 30;
      }
    }
    handleScore = depthComponent * 0.5 + lengthComponent * 0.3 + volumeBonus * 0.2;
  }

  // Redistribute the handle's 20% weight across the other five components when
  // no handle has formed yet, so an early cup-only pattern can still score well.
  const weights = handle
    ? { depth: 0.20, length: 0.15, roundness: 0.25, symmetry: 0.15, handle: 0.20, priorTrend: 0.05 }
    : { depth: 0.25, length: 0.19, roundness: 0.31, symmetry: 0.19, handle: 0, priorTrend: 0.06 };

  const rawConfidence =
    depthScore * weights.depth +
    lengthScore * weights.length +
    roundness * weights.roundness +
    symmetryScore * weights.symmetry +
    handleScore * weights.handle +
    priorTrendScore * weights.priorTrend;
  const confidence = Math.max(0, Math.min(100, Math.round(rawConfidence)));

  if (confidence < cfg.minConfidenceToShow) return null;

  return {
    leftRimIndex: rim, leftRimPrice: rimPrice,
    cupLowIndex: cupLowIdx, cupLowPrice,
    rightRimIndex: rightRimIdx, rightRimPrice,
    handle,
    breakoutPrice: (handle ? handle.startPrice : rimPrice) * 1.002,
    confidence,
    subScores: { depth: depthScore, length: lengthScore, roundness, symmetry: symmetryScore, handle: handleScore, priorTrend: priorTrendScore },
    hasHandle: handle !== null,
  };
}

/** Detects the single most-recent qualifying cup (with handle, if formed) in `bars`. */
export function computeCupHandle(bars: KBar[], cfg: CupHandleSettings): CupHandlePattern | null {
  const n = bars.length;
  if (n < cfg.rimWindowBars + cfg.minLengthBars) return null;

  const highs = bars.map((b) => b.high);
  const lows = bars.map((b) => b.low);
  const closes = bars.map((b) => b.close);
  const vols = bars.map((b) => b.volume);

  const pivotHigh = findPivotHighs(highs, cfg.pivotBars);

  const candidates: number[] = [];
  for (let r = cfg.pivotBars; r < n - cfg.pivotBars; r++) {
    if (!pivotHigh[r]) continue;
    const compStart = Math.max(0, r - 2 * cfg.rimWindowBars);
    if (highs[r] < maxIn(highs, compStart, r)) continue;
    const legStart = Math.max(0, r - cfg.priorLegBars);
    const legLow = legStart < r ? minIn(lows, legStart, r) : highs[r];
    if (legLow <= 0 || highs[r] < legLow * (1 + cfg.minLegUpPct)) continue;
    candidates.push(r);
  }

  // Most recent qualifying pattern only — scan newest rim candidate first.
  for (let ci = candidates.length - 1; ci >= 0; ci--) {
    const pattern = evaluateCandidate(highs, lows, closes, vols, candidates[ci], cfg, n);
    if (pattern) return pattern;
  }
  return null;
}

// ── Drawing ─────────────────────────────────────────────────────────────────

const RIM_COLOR = 'rgba(146,193,131,0.9)';
const CUP_COLOR = 'rgba(96,165,250,0.9)';
const HANDLE_BORDER_COLOR = 'rgba(251,191,36,0.9)';
const HANDLE_FILL_COLOR = 'rgba(251,191,36,0.10)';
const BREAKOUT_COLOR = 'rgba(168,85,247,0.85)';

function confidenceColor(score: number): string {
  if (score >= 75) return '#22C55E';
  if (score >= 50) return '#F59E0B';
  return '#9CA3AF';
}

type CupHandleResult = { pattern: CupHandlePattern | null };

type DrawParams = IndicatorDrawParams<CupHandleResult> & {
  ctx: CanvasRenderingContext2D;
  visibleRange: { from: number; to: number };
  xAxis: { convertToPixel: (i: number) => number };
  yAxis: { convertToPixel: (v: number) => number };
  bounding: Bounding;
};

/** Smooth polyline through `points` (quadratic bezier via consecutive midpoints —
 * avoids the sharp corners a naive lineTo/lineTo chain produces). Assumes ctx has
 * already had beginPath() called and a style/dash set; only strokes at the end. */
function strokeSmoothPath(ctx: CanvasRenderingContext2D, points: { x: number; y: number }[]) {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  if (points.length === 2) {
    ctx.lineTo(points[1].x, points[1].y);
  } else {
    for (let i = 1; i < points.length - 1; i++) {
      const xc = (points[i].x + points[i + 1].x) / 2;
      const yc = (points[i].y + points[i + 1].y) / 2;
      ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
    }
    const n = points.length;
    ctx.quadraticCurveTo(points[n - 2].x, points[n - 2].y, points[n - 1].x, points[n - 1].y);
  }
  ctx.stroke();
}

/**
 * Cup floor curve — ported directly from the MarketSmith Pine Script source
 * (its "DRAWING CURRENT CUP" block): two exponential halves through 3 real
 * anchor prices (left rim, cup low, right rim), each nudged 1% for a small
 * clearance buffer, exactly as the Pine source does. The left half decays
 * from the rim down to the low (steep near the rim, flattening at the
 * bottom); the right half grows from the low back up to the rim (flat at
 * the bottom, steepening near the top) — a smooth mathematical bowl, not a
 * trace of the actual candle-by-candle path.
 */
function buildExponentialCupCurve(pattern: CupHandlePattern): { index: number; price: number }[] {
  const BUFFER = 0.01;
  const startPrice = pattern.leftRimPrice * (1 - BUFFER);
  const bottomPrice = pattern.cupLowPrice * (1 - BUFFER);
  const endPrice = pattern.rightRimPrice * (1 - BUFFER);
  const lengthLeft = pattern.cupLowIndex - pattern.leftRimIndex;
  const lengthRight = pattern.rightRimIndex - pattern.cupLowIndex;

  const points: { index: number; price: number }[] = [];

  // Left half: decaying exponential from startPrice down to bottomPrice.
  if (lengthLeft > 0 && startPrice > bottomPrice) {
    const k = 1 / (startPrice - bottomPrice);
    for (let i = 0; i <= lengthLeft; i++) {
      let price: number;
      if (i === 0) price = startPrice;
      else if (i === lengthLeft) price = bottomPrice;
      else price = bottomPrice + (1 / Math.exp((i * 6) / lengthLeft)) / k;
      points.push({ index: pattern.leftRimIndex + i, price });
    }
  } else {
    points.push({ index: pattern.leftRimIndex, price: startPrice });
    points.push({ index: pattern.cupLowIndex, price: bottomPrice });
  }

  // Right half: growing exponential from bottomPrice up to endPrice.
  if (lengthRight > 0 && endPrice > bottomPrice) {
    const k = Math.exp(6) / (endPrice - bottomPrice);
    for (let i = 1; i <= lengthRight; i++) {
      const price = bottomPrice + (Math.exp((i * 6) / lengthRight) - 1) / k;
      points.push({ index: pattern.cupLowIndex + i, price });
    }
  } else {
    points.push({ index: pattern.rightRimIndex, price: endPrice });
  }

  return points;
}

type TooltipParams = {
  indicator: { result: CupHandleResult[] };
  crosshair: { dataIndex?: number };
};

const cupHandle: IndicatorTemplate<CupHandleResult> = {
  name: 'CUP_HANDLE',
  shortName: 'Cup & Handle',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,
  figures: [],

  calc: (dataList) => {
    const bars = dataList as unknown as KBar[];
    const settings = useChartStore.getState().cupHandleSettings;
    const pattern = computeCupHandle(bars, settings);
    return bars.map(() => ({ pattern }));
  },

  draw: (params: unknown): boolean => {
    const { ctx, visibleRange, bounding, xAxis, yAxis, indicator } = params as DrawParams;
    const results = indicator.result;
    if (!results.length) return true;
    const pattern = results[Math.min(visibleRange.to, results.length - 1)]?.pattern ?? results[results.length - 1]?.pattern;
    if (!pattern) return true;

    const { from, to } = visibleRange;
    // Skip entirely if the pattern is fully outside the visible window.
    if (pattern.leftRimIndex > to || (pattern.handle?.endIndex ?? pattern.rightRimIndex) < from) return true;

    ctx.save();

    const xLeft = xAxis.convertToPixel(pattern.leftRimIndex);
    const xRight = xAxis.convertToPixel(pattern.rightRimIndex);

    // ── Cup floor: exponential curve ported from the Pine source (see
    // buildExponentialCupCurve) — smooth by construction, needs no smoothing
    // pass, but still routed through strokeSmoothPath for consistency.
    const curvePoints = buildExponentialCupCurve(pattern)
      .map((p) => ({ x: xAxis.convertToPixel(p.index), y: yAxis.convertToPixel(p.price) }));
    ctx.strokeStyle = CUP_COLOR;
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    strokeSmoothPath(ctx, curvePoints);

    const yRim = yAxis.convertToPixel(pattern.leftRimPrice);

    // ── Dotted rim line ──────────────────────────────────────────────────
    ctx.strokeStyle = RIM_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(xLeft, yRim);
    ctx.lineTo(xRight, yRim);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── Handle box ───────────────────────────────────────────────────────
    if (pattern.handle) {
      const xHStart = xAxis.convertToPixel(pattern.handle.startIndex);
      const xHEnd = xAxis.convertToPixel(pattern.handle.endIndex);
      const yHLow = yAxis.convertToPixel(pattern.handle.lowPrice);
      const yHTop = yAxis.convertToPixel(pattern.handle.startPrice);
      ctx.fillStyle = HANDLE_FILL_COLOR;
      ctx.fillRect(xHStart, yHTop, Math.max(1, xHEnd - xHStart), yHLow - yHTop);
      ctx.strokeStyle = HANDLE_BORDER_COLOR;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.strokeRect(xHStart, yHTop, Math.max(1, xHEnd - xHStart), yHLow - yHTop);
      ctx.setLineDash([]);
    }

    // ── Breakout / pivot line ───────────────────────────────────────────
    const yBreakout = yAxis.convertToPixel(pattern.breakoutPrice);
    const breakoutStartX = xAxis.convertToPixel(pattern.handle?.endIndex ?? pattern.rightRimIndex);
    const rightEdgeX = xAxis.convertToPixel(to);
    ctx.strokeStyle = BREAKOUT_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    ctx.moveTo(breakoutStartX, yBreakout);
    ctx.lineTo(Math.max(breakoutStartX, rightEdgeX), yBreakout);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = BREAKOUT_COLOR;
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText(`Buy ${pattern.breakoutPrice.toFixed(2)}`, rightEdgeX - 2, yBreakout - 2);

    // ── Confidence badge ────────────────────────────────────────────────
    // Drop below the floating ChartLegend overlay (symbol/OHLCV header +
    // indicator rows) instead of a fixed y=8 — that overlay can grow much
    // taller than 8px (long symbol names wrap on mobile, more indicators add
    // rows), and a fixed offset gets run over by it.
    const badgeColor = confidenceColor(pattern.confidence);
    const label = pattern.hasHandle ? `Cup & Handle · ${pattern.confidence}%` : `Cup forming · ${pattern.confidence}%`;
    const legendHeight = useChartStore.getState().legendHeight;
    const badgeY = Math.max(8, legendHeight + 4);
    ctx.fillStyle = badgeColor;
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(label, bounding.width - 4, badgeY);

    ctx.restore();
    return true;
  },

  createTooltipDataSource: (params: unknown) => {
    const p = params as TooltipParams;
    const idx = p.crosshair.dataIndex ?? p.indicator.result.length - 1;
    const pattern = p.indicator.result[idx]?.pattern ?? p.indicator.result[p.indicator.result.length - 1]?.pattern;
    if (!pattern) return { name: 'Cup & Handle', calcParamsText: '', icons: [], values: [] };

    const values: { title: string; value: string }[] = [
      { title: 'Confidence: ', value: `${pattern.confidence}%` },
      { title: 'Depth fit: ', value: `${Math.round(pattern.subScores.depth)}` },
      { title: 'Length fit: ', value: `${Math.round(pattern.subScores.length)}` },
      { title: 'U-shape: ', value: `${Math.round(pattern.subScores.roundness)}` },
      { title: 'Symmetry: ', value: `${Math.round(pattern.subScores.symmetry)}` },
      { title: 'Prior trend: ', value: `${Math.round(pattern.subScores.priorTrend)}` },
    ];
    if (pattern.hasHandle) {
      values.push({ title: 'Handle: ', value: `${Math.round(pattern.subScores.handle)}` });
    }
    return { name: 'Cup & Handle', calcParamsText: '', icons: [], values };
  },
};

export default cupHandle;
